import { useState } from 'react';
import { useParams } from 'react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import { GenericCard } from '@/components/molecules/GenericCard';
import { PokemonPicker } from '@/components/molecules/PokemonPicker';
import { SelectedPokemonPanel } from '@/components/molecules/SelectedPokemonPanel';
import { Loader } from '@/components/atoms/Loader';
import { type OwnedPokemon, getUltraBreche } from '@/services/pokemon';
import { fetchInstance } from '@/utils/fetchInstance';

const selectUltraBrechePokemon = (webToken: string, allyOwnedPokemonId: string) =>
  fetchInstance.put<{ ok: boolean }>(`pokemons/ultra-breche/${webToken}`, { allyOwnedPokemonId });

const TerminalState = ({ message }: { message: string }) => (
  <main className="bg-warm flex min-h-screen items-center justify-center p-5">
    <div className="w-full max-w-md text-center">
      <p className="mb-4 text-5xl">🌀</p>
      <h2 className="text-content font-lexend mb-2 text-xl font-semibold">Ultra Brèche</h2>
      <p className="text-content/60 text-sm">{message}</p>
    </div>
  </main>
);

export const UltraBreche = () => {
  const { webToken } = useParams<{ webToken: string }>();

  const [selected, setSelected] = useState<OwnedPokemon | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const { data: combat, isLoading, isError } = useQuery({
    queryKey: ['ultraBreche', webToken],
    queryFn: () => getUltraBreche(webToken!),
    enabled: !!webToken,
    refetchOnWindowFocus: false,
  });

  const { mutate: confirm, isPending: isConfirming } = useMutation<
    { ok: boolean },
    Error,
    string
  >({
    mutationFn: (allyOwnedPokemonId) => selectUltraBrechePokemon(webToken!, allyOwnedPokemonId),
    onSuccess: () => setConfirmed(true),
    onError: (err) => setApiError(err.message),
  });

  if (isLoading) {
    return (
      <main className="bg-warm flex min-h-screen items-center justify-center">
        <Loader size={48} color="#f29602" />
      </main>
    );
  }

  if (isError || !combat) {
    return <TerminalState message="Ce lien est invalide ou introuvable." />;
  }

  if (confirmed) {
    return <TerminalState message="Pokémon sélectionné ! Le combat a démarré sur Discord." />;
  }

  if (combat.status !== 'pending') {
    return (
      <TerminalState
        message={
          combat.status === 'active'
            ? 'Le combat est déjà en cours sur Discord.'
            : 'Ce combat est terminé.'
        }
      />
    );
  }

  return (
    <main className="bg-warm min-h-screen">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-5">
        {combat.enemy && (
          <div className="flex items-center gap-4 rounded-2xl bg-surface px-5 py-4 shadow-sm">
            <img src={combat.enemy.sprite} alt={combat.enemy.realname} className="h-16 w-16 object-contain" />
            <div>
              <p className="text-content/50 text-xs">Adversaire</p>
              <p className="font-lexend text-content text-base font-semibold">
                {combat.enemy.realname}
                {combat.enemy.shiny && ' ✨'}
              </p>
              <p className="text-content/60 text-sm">Niv. {combat.enemy.level}</p>
            </div>
          </div>
        )}

        <GenericCard
          title="Choisir un Pokémon allié"
          subtitle="Sélectionne le Pokémon qui combattra dans l'Ultra Brèche"
        >
          <PokemonPicker
            onSelect={setSelected}
            selectedId={selected?.ownedPokemonId}
            queryKey={`ultra-breche-${webToken}`}
          />

          {apiError && (
            <div className="mt-4 rounded-xl bg-red-500/10 px-4 py-3">
              <p className="text-sm font-medium text-red-600">{apiError}</p>
            </div>
          )}

          <SelectedPokemonPanel
            selected={selected}
            loading={isConfirming}
            onConfirm={() => {
              if (!selected) return;
              setApiError(null);
              confirm(selected.ownedPokemonId);
            }}
            className="mt-4"
          />
        </GenericCard>
      </div>
    </main>
  );
};
